import React from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Image from 'react-bootstrap/Image';
import { useTranslation } from 'react-i18next';

import Seo from '../components/Seo';

import '../style/about.css';
import imageAbout from '../assets/images/pexels-kevin-ku-92347-577585.jpg';
import expertiseImage from '../assets/images/ai-2.jpg';
import roniPro from '../assets/images/roni-pro.png';
import pierrePro from '../assets/images/pierre-pro.png';

function AboutPage() {
  const { t } = useTranslation();

  const teamMemberImages = {
    1: roniPro,
    2: pierrePro
  };

  return (
    <div className="about-page">
      <Seo path='/about' titleKey='seo.about.title' descriptionKey='seo.about.description' />
      <main>
        {/* Hero Section */}
        <section className="hero-section">
          <Container>
            <Row className="align-items-center">
              <Col lg={6} className="mb-4 mb-lg-0">
                <h1>{t('aboutPage.hero.title')}</h1>
                <p className="lead">{t('aboutPage.hero.subtitle')}</p>
                <p>{t('aboutPage.hero.description')}</p>
              </Col>
              <Col lg={6}>
                <div className="hero-image-container">
                  <Image 
                    src={imageAbout} 
                    alt={t('aboutPage.hero.imageAlt')} 
                    fluid 
                    className="hero-image"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Mission and Vision */}
        <section className="mission-vision-section">
          <Container>
            <h2 className="section-title text-center">{t('aboutPage.missionVision.title')}</h2>
            <Row className="mt-5">
              <Col md={6} className="mb-4 mb-md-0">
                <div className="mission-card">
                  <h3>{t('aboutPage.missionVision.mission.title')}</h3>
                  <p>{t('aboutPage.missionVision.mission.description')}</p>
                </div>
              </Col>
              <Col md={6}>
                <div className="vision-card">
                  <h3>{t('aboutPage.missionVision.vision.title')}</h3>
                  <p>{t('aboutPage.missionVision.vision.description')}</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Core Values */}
        <section className="core-values-section">
          <Container>
            <h2 className="section-title text-center">{t('aboutPage.coreValues.title')}</h2>
            <p className="section-subtitle text-center">{t('aboutPage.coreValues.subtitle')}</p>
            
            <Row className="mt-5">
              {[1, 2, 3, 4].map((item) => (
                <Col lg={3} md={6} className="mb-4" key={item}>
                  <Card className="value-card h-100">
                    <Card.Body className="text-center">
                      <div className="value-icon">
                        <i className={t(`aboutPage.coreValues.values.value${item}.icon`)}></i>
                      </div>
                      <Card.Title>{t(`aboutPage.coreValues.values.value${item}.title`)}</Card.Title>
                      <Card.Text>
                        {t(`aboutPage.coreValues.values.value${item}.description`)}
                      </Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Team Section */}
        <section className="team-section">
          <Container>
            <h2 className="section-title text-center">{t('aboutPage.team.title')}</h2>
            <p className="section-subtitle text-center">{t('aboutPage.team.subtitle')}</p>
            
            <Row className="mt-5 justify-content-center">
              {[1, 2].map((member) => (
                <Col lg={4} md={6} className="mb-4" key={member}>
                  <Card className="team-card h-100">
                    <div className="team-member-img">
                      <Image 
                        src={teamMemberImages[member]} 
                        alt={t(`aboutPage.team.members.member${member}.name`)} 
                        fluid 
                        className="member-img"
                      />
                    </div>
                    <Card.Body className="text-center">
                      <Card.Title>{t(`aboutPage.team.members.member${member}.name`)}</Card.Title>
                      <Card.Subtitle className="mb-2 text-muted">
                        {t(`aboutPage.team.members.member${member}.position`)}
                      </Card.Subtitle>
                      <Card.Text>
                        {t(`aboutPage.team.members.member${member}.bio`)}
                      </Card.Text>
                      <div className="social-links">
                        <a href={t(`aboutPage.team.members.member${member}.linkedin`)} target="_blank" rel="noopener noreferrer">
                          <i className="fab fa-linkedin"></i>
                        </a>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section>

        {/* Expertise */}
        <section className="expertise-section">
          <Container>
            <h2 className="section-title text-center">{t('aboutPage.expertise.title')}</h2>
            <p className="section-subtitle text-center">{t('aboutPage.expertise.subtitle')}</p>
            
            <Row className="mt-5">
              <Col lg={6} className="mb-4 mb-lg-0">
                <p>{t('aboutPage.expertise.description')}</p>
                <div className="skills-container">
                  {[1, 2, 3, 4, 5, 6, 7].map((skill) => (
                    <div className="skill-item" key={skill}>
                      <div className="d-flex justify-content-between">
                        <span>{t(`aboutPage.expertise.skills.skill${skill}.name`)}</span>
                        <span>{t(`aboutPage.expertise.skills.skill${skill}.percentage`)}%</span>
                      </div>
                      <ProgressBar 
                        now={parseInt(t(`aboutPage.expertise.skills.skill${skill}.percentage`))} 
                        className="skill-progress"
                      />
                    </div>
                  ))}
                </div>
              </Col>
              <Col lg={6}>
                <div className="expertise-image-container">
                  <Image 
                    src={expertiseImage} 
                    alt={t('aboutPage.expertise.imageAlt')} 
                    fluid 
                    className="expertise-image"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Testimonials */}
        {/* <section className="testimonials-section">
          <Container>
            <h2 className="section-title text-center">{t('aboutPage.testimonials.title')}</h2>
            <p className="section-subtitle text-center">{t('aboutPage.testimonials.subtitle')}</p>
            
            <Row className="mt-5">
              {[1, 2, 3].map((testimonial) => (
                <Col lg={4} className="mb-4" key={testimonial}>
                  <Card className="testimonial-card h-100">
                    <Card.Body>
                      <div className="testimonial-quote">
                        <i className="fas fa-quote-left"></i>
                      </div>
                      <Card.Text className="testimonial-text">
                        {t(`aboutPage.testimonials.items.testimonial${testimonial}.quote`)}
                      </Card.Text>
                      <div className="testimonial-author d-flex align-items-center">
                        <div className="testimonial-author-img">
                          <Image 
                            src={`/images/about/testimonial-${testimonial}.jpg`} 
                            alt={t(`aboutPage.testimonials.items.testimonial${testimonial}.name`)} 
                            roundedCircle 
                          />
                        </div>
                        <div className="testimonial-author-info">
                          <h5>{t(`aboutPage.testimonials.items.testimonial${testimonial}.name`)}</h5>
                          <p className="mb-0">{t(`aboutPage.testimonials.items.testimonial${testimonial}.position`)}, {t(`aboutPage.testimonials.items.testimonial${testimonial}.company`)}</p>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Container>
        </section> */}
      </main>
    </div>
  );
}

export default AboutPage;
